import esriConfig from '@arcgis/core/config';
import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import Graphic from '@arcgis/core/Graphic';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import {
    faCopy,
    faExclamationCircle,
    faExternalLinkSquareAlt,
    faFolderOpen,
    faPaste,
    faSave,
    faTimes,
    faTrashAlt
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './App.module.css';
import MapTip from './arcgis/components/MapTip';
import useMap from './arcgis/hooks/useMap';
import useMapView from './arcgis/hooks/useMapView';
import {
    queryFeatureLayer,
    zoomToExtent,
    zoomToFeatures,
    zoomToLayer
} from './arcgis/jsapi/js-api-util';
import CopyFeaturesModal from './components/CopyFeaturesModal';
import LoadFavouriteModal from './components/LoadFavouriteModal';
import PasteFeaturesModal from './components/PasteFeaturesModal';
import SaveFavouriteModal from './components/SaveFavouriteModal';
import useAppConfig from './hooks/useAppConfig';
import useFavourites from './hooks/useFavourites';
import useFeatures from './hooks/useFeatures';
import useFilters from './hooks/useFilters';
import useMapWidgets from './hooks/useMapWidgets';
import useModals from './hooks/useModals';
import useReports from './hooks/useReports';
import useSelector from './hooks/useSelector';
import useSketchModel from './hooks/useSketchModel';

let highlight: any;
const App = ({ config }: { config?: any }) => {
    // AGOL Configurable App.
    const appConfig = useAppConfig(config);
    // Esri config.
    /* 
    I removed this until its required for internal only sites
    To reinstate add this to package.json and uncomment esriConfig.assetsPath = `${process.env.PUBLIC_URL}/assets`
    
    "scripts": {
        "start": "npm run copy-arcgis-assets && react-scripts start",
        "build": "npm run copy-arcgis-assets && node ./scripts/build-js-bundle.js",
        "postbuild": "node ./scripts/build-css-bundle.js",
        "test": "react-scripts test",
        "eject": "react-scripts eject",
        "copy-arcgis-assets": "ncp ./node_modules/@arcgis/core/assets ./public/assets"
      },
    
    see https://esriuk.visualstudio.com/alt-instantatlas/_sprints/backlog/alt-instantatlas%20Team/alt-instantatlas/Sprints%202022/Q3%20Sprint%206?workitem=20935
    */
    if (
        appConfig !== undefined &&
        appConfig !== null &&
        appConfig.host !== undefined &&
        appConfig.host !== null &&
        appConfig.host !== ''
    ) {
        esriConfig.assetsPath = `${appConfig.host.replace(
            /^(.*)\/$/,
            '$1'
        )}/assets`;
    }
    esriConfig.portalUrl = appConfig?.portalUrl;

    // App Config "activeToolName" changed.
    useEffect(() => {
        if (appConfig?.activeToolName !== undefined)
            setTool(appConfig.activeToolName);
    }, [appConfig]);

    // Modal visibility state.
    const [modal, showModal, hideModals] = useModals();

    // To hold modals.
    const modalRef: any = useRef();

    // Handle Add/Remove/Fetch from local storage.
    const [
        favourites,
        addFeatureFavourite,
        addSelectorFavourite,
        removeFavourite
    ] = useFavourites();

    // Map state.
    const mapRef: any = useRef();
    const map = useMap({
        webmapId: appConfig?.webmap,
        basemap:
            appConfig?.webmap === undefined ? appConfig?.basemap : undefined
    });
    const view = useMapView({ map, container: mapRef?.current });
    const [activeLayer, setActiveLayer] = useState<any>();
    const [activeLayerView, setActiveLayerView] = useState<any>();

    // Report state.
    const [reports, layerInfos, activeLayerInfo, setActiveLayerInfo] =
        useReports({
            map,
            view,
            reportIds: appConfig?.reportIds,
            layerIndex: appConfig?.layerIndex,
            mastertable: appConfig?.mastertable
        });

    // Map tip function
    const mapTip = useCallback(
        (feature: any) => {
            return feature.attributes[activeLayerInfo.nameField];
        },
        [activeLayerInfo]
    );

    // Filter state.
    const [
        filterFields,
        filterFieldName,
        filterFieldValues,
        filterFieldValue,
        setFilterFieldName,
        setFilterFieldValue,
        clearFilter
    ] = useFilters(activeLayer, appConfig?.fieldFilterList);

    // Features state.
    const maxFeatures =
        appConfig?.useFeatureLimit === true &&
        appConfig?.maxFeatures !== undefined
            ? appConfig?.maxFeatures
            : undefined;
    const [
        features,
        ids,
        names,
        setFeatures,
        addFeatures,
        clearFeatures,
        removeFeature,
        differenceFeatures
    ] = useFeatures(
        activeLayerInfo?.idField,
        activeLayerInfo?.nameField,
        maxFeatures
    );

    // Selector state.
    const [selector, setSelector, clearSelector] = useSelector();

    // Map widget state.
    useMapWidgets({
        appConfig,
        map,
        view,
        layer: activeLayer,
        nameField: activeLayerInfo?.nameField
    });

    // Custom map widgets.
    const onDrawStart = useCallback((event: any, sketch: any) => {
        sketch.layer.removeAll();
    }, []);
    const onDrawEnd = useCallback(
        async (event: any, sketch: any) => {
            if (activeLayerView !== undefined) {
                if (event.tool === 'point') sketch.layer.remove(event.graphic);
                else
                    setSelector({
                        tool: event.tool,
                        geometry: event.graphic.geometry
                    });

                // Get the feature selection.
                const query = activeLayerView.createQuery();
                query.spatialRelationship = 'intersects';
                query.geometry = event.graphic.geometry;
                query.outFields = activeLayerView.availableFields;
                query.returnGeometry = true;
                const response = await activeLayerView.queryFeatures(query);

                // Point tool allows us to add/remove
                // features to/from the current selection.
                if (event.tool === 'point')
                    differenceFeatures(response.features);
                // All the other tools add features to
                // the current selection.
                else addFeatures(response.features);

                sketch.create(event.tool);
            }
        },
        [activeLayerView, addFeatures, differenceFeatures, setSelector]
    );

    const [tool, setTool] = useState<string>('point');
    const [showFilter, setShowFilter] = useState<boolean>(false);
    const sketchRef: any = useRef();
    const filterRef: any = useRef();
    const [sketchModel, sketchLayer] = useSketchModel({
        map,
        view,
        onDrawStart,
        onDrawEnd
    });

    useEffect(() => {
        if (sketchModel !== undefined && tool !== undefined)
            sketchModel.create(tool);
    }, [sketchModel, tool]);

    useEffect(() => {
        if (
            view?.ui !== null &&
            view?.ui !== undefined &&
            view.ui.find('sketch') === null
        )
            view.ui.add(sketchRef?.current, 'top-right');
        if (
            view?.ui !== null &&
            view?.ui !== undefined &&
            view.ui.find('filter') === null
        )
            view.ui.add(filterRef?.current, 'top-right');
    }, [view]);

    useEffect(() => {
        if (showFilter === false) clearFilter();
    }, [clearFilter, showFilter]);

    const clearSelection = useCallback(() => {
        sketchLayer.removeAll();
        clearFeatures();
        clearSelector();
    }, [clearFeatures, clearSelector, sketchLayer]);

    // Clear everything from map and update feature selection.
    // Used when ids are pasted in or feature favourites are loaded.
    // arrFeatures: List of feature ids or names.
    const setFeatureIds = async (ids: string[]) => {
        clearSelection();
        clearFilter();
        const response = await queryFeatureLayer({
            layer: activeLayer,
            where: `${activeLayerInfo.idField} IN ('${ids.join("','")}') OR ${
                activeLayerInfo.nameField
            } IN ('${ids.join("','")}') `,
            returnGeometry: true
        });
        setFeatures(response.features);
        if (view !== undefined)
            zoomToFeatures({ view, features: response.features });
    };

    /// Layer changed.
    useEffect(() => {
        const onLayerChanged = async () => {
            if (
                appConfig !== undefined &&
                view !== undefined &&
                map !== undefined &&
                layerInfos !== undefined &&
                activeLayerInfo?.id !== undefined
            ) {
                // Make only active report layer visible
                layerInfos.forEach((lyrInfo: any) => {
                    if (lyrInfo.layer !== undefined)
                        lyrInfo.layer.visible = false;
                });
                const layer = map.layers.find(
                    (lyr: any) => lyr.id === activeLayerInfo?.id
                ) as FeatureLayer;

                if (layer !== undefined) {
                    layer.visible = true;

                    // Zoom to a scale where the layer is visible.
                    // Note that the methods are diferent.
                    // No idea why but this is the only way it would work.
                    if (layer.minScale !== 0 && view.scale > layer.minScale)
                        view.scale = layer.minScale;
                    else if (
                        layer.maxScale !== 0 &&
                        view.scale < layer.maxScale
                    )
                        view.goTo({ scale: layer.maxScale });
                    else if (appConfig.zoomToLayer === true)
                        zoomToLayer({ view, layer });

                    // Get layerView.
                    const layerView = await view.whenLayerView(layer);
                    setActiveLayer(layer);
                    setActiveLayerView(layerView);
                }
            }
        };
        onLayerChanged();
    }, [activeLayerInfo, appConfig, layerInfos, map, view]);

    // Filter changed.
    useEffect(() => {
        const onFilterChanged = async () => {
            if (
                view !== undefined &&
                filterFieldName !== '' &&
                sketchLayer !== undefined &&
                activeLayerView !== undefined
            ) {
                clearSelection();
                if (filterFieldValue !== '--') {
                    activeLayerView.filter = {
                        where: `${filterFieldName} = '${filterFieldValue}'`
                    };
                    const response = await queryFeatureLayer({
                        layer: activeLayer,
                        where: `${filterFieldName} = '${filterFieldValue}'`,
                        returnGeometry: true
                    });
                    await zoomToFeatures({ view, features: response.features });
                } else activeLayerView.filter = null;
            }
        };
        onFilterChanged();
    }, [
        activeLayer,
        activeLayerView,
        clearSelection,
        filterFieldName,
        filterFieldValue,
        sketchLayer,
        view
    ]);

    // Feature selection changed.
    useEffect(() => {
        if (activeLayerView !== undefined) {
            highlight?.remove();
            highlight = activeLayerView.highlight(features);
        }
    }, [activeLayerView, features]);

    return (
        <>
            <div
                id="filter"
                ref={filterRef}
                className={styles.filter}
                style={
                    view?.ui === null ||
                    view?.ui.find('filter') === null ||
                    view?.ui.find('filter') === undefined ||
                    showFilter === false
                        ? { display: 'none' }
                        : {}
                }
            >
                <div>
                    <span
                        onClick={() => setShowFilter(false)}
                        className={styles.filterClose}
                        title="Clear Filter"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </span>
                </div>
                <label htmlFor="filterField" className="sr-only">
                    Filter field:
                </label>
                <select
                    name="filterField"
                    id="filterField"
                    value={filterFieldName}
                    onChange={(evt: any) => {
                        setFilterFieldName(evt.target.value);
                    }}
                >
                    {filterFields.map((fld: any) => {
                        return (
                            <option key={fld.name} value={fld.name}>
                                {fld.alias}
                            </option>
                        );
                    })}
                </select>
                <label htmlFor="filterFieldValues" className="sr-only">
                    Filter value:
                </label>
                <select
                    name="filterFieldValues"
                    id="filterFieldValues"
                    value={filterFieldValue}
                    onChange={(evt: any) => {
                        setFilterFieldValue(evt.target.value);
                    }}
                >
                    <option key="--" value="--">
                        --
                    </option>
                    {filterFieldValues.map((value: any) => {
                        return (
                            <option key={value} value={value}>
                                {value}
                            </option>
                        );
                    })}
                </select>
            </div>
            <div
                id="sketch"
                ref={sketchRef}
                className={styles.sketch}
                style={
                    view?.ui === null ||
                    view?.ui.find('sketch') === null ||
                    view?.ui.find('sketch') === undefined
                        ? { display: 'none' }
                        : {}
                }
            >
                {appConfig?.selectToolClick === true && (
                    <button
                        onClick={() => setTool('point')}
                        aria-label="Draw a point"
                        className={`${styles.btn} ${styles.sketchTool} ${
                            tool === 'point' && styles.sketchActiveTool
                        } esri-icon-map-pin`}
                        title="Draw a point"
                        type="button"
                    ></button>
                )}
                {appConfig?.selectToolBox === true && (
                    <button
                        onClick={() => setTool('rectangle')}
                        aria-label="Draw a rectangle"
                        className={`${styles.btn} ${styles.sketchTool} ${
                            tool === 'rectangle' && styles.sketchActiveTool
                        } esri-icon-checkbox-unchecked`}
                        title="Draw a rectangle"
                        type="button"
                    ></button>
                )}
                {appConfig?.selectToolCircle === true && (
                    <button
                        onClick={() => setTool('circle')}
                        aria-label="Draw a circle"
                        className={`${styles.btn} ${styles.sketchTool} ${
                            tool === 'circle' && styles.sketchActiveTool
                        } esri-icon-radio-unchecked`}
                        title="Draw a circle"
                        type="button"
                    ></button>
                )}
                {appConfig?.selectToolPolygon === true && (
                    <button
                        onClick={() => setTool('polygon')}
                        aria-label="Draw a polygon"
                        className={`${styles.btn} ${styles.sketchTool} ${
                            tool === 'polygon' && styles.sketchActiveTool
                        } esri-icon-polygon`}
                        title="Draw a polygon"
                        type="button"
                    ></button>
                )}
                {appConfig?.selectToolPolyline === true && (
                    <button
                        onClick={() => setTool('polyline')}
                        aria-label="Draw a polyline"
                        className={`${styles.btn} ${styles.sketchTool} ${
                            tool === 'polyline' && styles.sketchActiveTool
                        } esri-icon-polyline`}
                        title="Draw a polyline"
                        type="button"
                    ></button>
                )}
                {filterFields.length > 0 && (
                    <>
                        <div className={styles.sketchSeparator}></div>
                        <button
                            onClick={() => setShowFilter(!showFilter)}
                            aria-label="Filter Layer"
                            className={`${styles.btn} ${styles.sketchTool} esri-icon-filter`}
                            title="Filter Layer"
                            type="button"
                        ></button>
                    </>
                )}
                <div className={styles.sketchSeparator}></div>
                <button
                    onClick={() => clearSelection()}
                    aria-label="Clear Selection"
                    className={`${styles.btn} ${styles.sketchTool} ${
                        (features.length > 0 || selector !== null) &&
                        styles.sketchActiveTrash
                    } esri-icon-trash`}
                    title="Clear Selection"
                    type="button"
                ></button>
            </div>
            <div className={styles.app}>
                <div id="map" className={styles.map} ref={mapRef}>
                    <MapTip
                        view={view}
                        layer={activeLayer}
                        tip={mapTip}
                    ></MapTip>
                </div>
                <div className={styles.sidebar}>
                    <div className={styles.panel}>
                        <div className={styles.panelContainer}>
                            <div className={styles.toolbar}>
                                <label
                                    htmlFor="activeLayer"
                                    className="sr-only"
                                >
                                    Active layer:
                                </label>
                                <select
                                    type="select"
                                    name="activeLayer"
                                    id="activeLayer"
                                    className={styles.sketchSelect}
                                    value={activeLayerInfo?.id}
                                    onChange={(evt: any) => {
                                        clearSelection();
                                        setActiveLayerInfo(
                                            layerInfos.find(
                                                (lyrInfo: any) =>
                                                    lyrInfo.id ===
                                                    evt.target.value
                                            )
                                        );
                                    }}
                                >
                                    {layerInfos.map((lyrInfo: any) => {
                                        return (
                                            <option
                                                key={lyrInfo.id}
                                                value={lyrInfo.id}
                                            >
                                                {lyrInfo.title}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div className={styles.featureToolbar}>
                                {appConfig?.selectToolLoad === true && (
                                    <button
                                        className={styles.btn}
                                        title="Load a previously saved selection"
                                        aria-label="Load a previously saved selection"
                                        onClick={() => {
                                            showModal('load-favourite');
                                        }}
                                    >
                                        <span>
                                            <FontAwesomeIcon
                                                icon={faFolderOpen}
                                            />
                                        </span>
                                        <span>Load</span>
                                    </button>
                                )}
                                {appConfig?.selectToolPaste === true && (
                                    <button
                                        className={styles.btn}
                                        title="Paste selection"
                                        aria-label="Paste selection"
                                        onClick={() => {
                                            showModal('paste-features');
                                        }}
                                    >
                                        <span>
                                            <FontAwesomeIcon icon={faPaste} />
                                        </span>
                                        <span>Paste</span>
                                    </button>
                                )}
                                {appConfig?.selectToolPaste === true && (
                                    <button
                                        className={styles.btn}
                                        title="Copy selection"
                                        aria-label="Copy selection"
                                        onClick={() => {
                                            showModal('copy-features');
                                        }}
                                    >
                                        <span>
                                            <FontAwesomeIcon icon={faCopy} />
                                        </span>
                                        <span>Copy</span>
                                    </button>
                                )}
                            </div>
                            {selector === null &&
                                features.length === 0 &&
                                appConfig?.displayWelcomePanel === true &&
                                appConfig?.welcomePanelText !== undefined &&
                                appConfig?.welcomePanelText !== '' && (
                                    <div
                                        className={styles.help}
                                        dangerouslySetInnerHTML={{
                                            __html: appConfig?.welcomePanelText
                                        }}
                                    ></div>
                                )}
                            {selector !== null && (
                                <div className={styles.toolbar}>
                                    <button
                                        className={`${styles.btn} ${styles.label}`}
                                        title="Zoom to selector"
                                        aria-label="Zoom to selector"
                                        onClick={() => {
                                            if (view !== undefined)
                                                zoomToExtent({
                                                    view,
                                                    extent: selector.geometry
                                                        .extent
                                                });
                                        }}
                                    >
                                        {selector.label}
                                    </button>
                                    {appConfig?.selectToolLoad === true && (
                                        <button
                                            className={styles.btn}
                                            title="Save"
                                            aria-label="Save"
                                            disabled={selector === null}
                                            onClick={() => {
                                                showModal('save-selector');
                                            }}
                                        >
                                            <span>
                                                <FontAwesomeIcon
                                                    icon={faSave}
                                                />
                                            </span>
                                        </button>
                                    )}
                                </div>
                            )}
                            {features.length > 0 && (
                                <div className={styles.toolbar}>
                                    <button
                                        className={`${styles.btn} ${styles.label}`}
                                        title="Zoom to selection"
                                        aria-label="Zoom to selection"
                                        onClick={() => {
                                            if (view !== undefined)
                                                zoomToFeatures({
                                                    view,
                                                    features
                                                });
                                        }}
                                    >
                                        <span>
                                            {features.length}&nbsp;Areas
                                            Selected
                                            {maxFeatures !== undefined && (
                                                <>
                                                    &nbsp;&nbsp;&nbsp;
                                                    <FontAwesomeIcon
                                                        icon={
                                                            faExclamationCircle
                                                        }
                                                    />{' '}
                                                    Selection limited to{' '}
                                                    {maxFeatures} areas
                                                </>
                                            )}
                                        </span>
                                    </button>
                                    {appConfig?.selectToolLoad === true && (
                                        <button
                                            className={styles.btn}
                                            title="Save"
                                            aria-label="Save"
                                            onClick={() => {
                                                showModal('save-features');
                                            }}
                                        >
                                            <span>
                                                <FontAwesomeIcon
                                                    icon={faSave}
                                                />
                                            </span>
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className={styles.list}>
                                {features.map((feature: any, index: number) => {
                                    const id: string = ids[index];
                                    const name: string = names[index];
                                    return (
                                        <div
                                            className={styles.listItem}
                                            key={id}
                                        >
                                            <button
                                                className={`${styles.btn} ${styles.label}`}
                                                title="Zoom to area"
                                                aria-label="Zoom to area"
                                                onClick={() => {
                                                    if (view !== undefined)
                                                        zoomToFeatures({
                                                            view,
                                                            features: [feature]
                                                        });
                                                }}
                                            >
                                                {id}&nbsp;{name}
                                            </button>
                                            <button
                                                className={styles.btn}
                                                title="Remove"
                                                aria-label="Remove"
                                                onClick={() => {
                                                    removeFeature(id);
                                                }}
                                            >
                                                <span>
                                                    <FontAwesomeIcon
                                                        icon={faTrashAlt}
                                                    />
                                                </span>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>Reports</div>
                        <div className={styles.panelContainer}>
                            <div className={styles.list}>
                                {reports.map((report: any) => {
                                    if (
                                        report.layerUrl === activeLayerInfo?.url
                                    ) {
                                        if (features.length === 0) {
                                            return (
                                                <div
                                                    className={
                                                        styles.listItemDisabled
                                                    }
                                                    key={report.id}
                                                >
                                                    <span
                                                        className={styles.label}
                                                    >
                                                        {report.title}
                                                    </span>
                                                    <span
                                                        className={styles.icon}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={
                                                                faExternalLinkSquareAlt
                                                            }
                                                        />
                                                    </span>
                                                </div>
                                            );
                                        } else {
                                            let url: string = `${appConfig?.reportUrlPattern}/`;

                                            // <report-id1>;<core-layer-id1>;<id-field1>;<name-field1>,
                                            // <report-id2>;<core-layer-id2>;<id-field2>;<name-field2>
                                            // ,...,<report-idN>;<core-layer-idN>;<id-fieldN>;<name-fieldN>
                                            if (
                                                appConfig?.reportIds.indexOf(
                                                    ';'
                                                ) !== -1 &&
                                                appConfig?.mastertable
                                            ) {
                                                if (features.length > 1)
                                                    url += `${
                                                        report.id
                                                    }/aggregate?features=${ids.join(
                                                        ','
                                                    )}&catalog=${
                                                        appConfig?.mastertable
                                                    }&layer=${
                                                        activeLayerInfo.geoId
                                                    }`;
                                                else
                                                    url += `${report.id}/${ids[0]}?catalog=${appConfig?.mastertable}&layer=${activeLayerInfo.geoId}`;
                                            }
                                            // <report-id1>,<report-id2>,...,<report-idN>
                                            else {
                                                if (features.length > 1)
                                                    url += `${
                                                        report.id
                                                    }/aggregate?features=${ids.join(
                                                        ','
                                                    )}`;
                                                else
                                                    url += `${report.id}/${ids[0]}`;
                                            }

                                            return (
                                                <div
                                                    className={styles.listItem}
                                                    key={report.id}
                                                >
                                                    <button
                                                        className={`${styles.btn} ${styles.label}`}
                                                        title="Click to open report"
                                                        aria-label="Click to open report"
                                                        onClick={() => {
                                                            window.open(
                                                                url,
                                                                '_blank'
                                                            );
                                                        }}
                                                    >
                                                        <span
                                                            className={
                                                                styles.label
                                                            }
                                                        >
                                                            {report.title}
                                                        </span>
                                                        <span
                                                            className={
                                                                styles.icon
                                                            }
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    faExternalLinkSquareAlt
                                                                }
                                                            />
                                                        </span>
                                                    </button>
                                                </div>
                                            );
                                        }
                                    } else return null;
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div
                id="car-modals"
                className={`${styles.modals}`}
                ref={modalRef}
                onClick={() => {
                    hideModals();
                }}
            ></div>
            {appConfig?.selectToolPaste === true && (
                <>
                    <PasteFeaturesModal
                        container={modalRef?.current?.id}
                        isOpen={modal === 'paste-features'}
                        onCancel={() => {
                            hideModals();
                        }}
                        onSelect={async (strFeatures: string) => {
                            hideModals();

                            // Rows? Columns? Do some guessing.
                            const rows = strFeatures.split(
                                strFeatures.indexOf('\n') > 0
                                    ? '\n'
                                    : strFeatures.indexOf('\t') > 0
                                    ? '\t'
                                    : ','
                            );
                            const arrFeatures = rows.map((r: string) => {
                                const id: string =
                                    r.indexOf('\t') > 0
                                        ? r.split('\t')[0]
                                        : r.split(',')[0];
                                return id.trim();
                            });
                            await setFeatureIds(arrFeatures);
                        }}
                    />
                    <CopyFeaturesModal
                        container={modalRef?.current?.id}
                        ids={ids}
                        isOpen={modal === 'copy-features'}
                        onClose={() => {
                            hideModals();
                        }}
                    />
                </>
            )}
            {features.length > 0 && appConfig?.selectToolLoad === true && (
                <SaveFavouriteModal
                    container={modalRef?.current?.id}
                    saveAs={activeLayerInfo?.title}
                    isOpen={modal === 'save-features'}
                    onCancel={() => {
                        hideModals();
                    }}
                    onSave={(label: string) => {
                        hideModals();
                        addFeatureFavourite({
                            layerUrl: activeLayerInfo.url,
                            layerLabel: activeLayerInfo.title,
                            label,
                            ids: [...ids]
                        });
                    }}
                />
            )}
            {selector !== null && appConfig?.selectToolLoad === true && (
                <SaveFavouriteModal
                    container={modalRef?.current?.id}
                    saveAs={selector?.label}
                    isOpen={modal === 'save-selector'}
                    onCancel={() => {
                        hideModals();
                    }}
                    onSave={(label: string) => {
                        hideModals();
                        addSelectorFavourite({
                            label,
                            tool: selector.tool,
                            geometry: selector.geometry
                        });
                    }}
                />
            )}
            {appConfig?.selectToolLoad === true && (
                <LoadFavouriteModal
                    container={modalRef?.current?.id}
                    favourites={favourites}
                    layerUrl={activeLayerInfo?.url}
                    isOpen={modal === 'load-favourite'}
                    onCancel={() => {
                        hideModals();
                    }}
                    onFeaturesSelect={async (ids: any[]) => {
                        hideModals();
                        await setFeatureIds(ids);
                    }}
                    onSelectorSelect={async (favourite: any) => {
                        hideModals();
                        sketchLayer.removeAll();

                        let geometry;
                        if (favourite.geometry.rings !== undefined)
                            geometry = Polygon.fromJSON(favourite.geometry);
                        else geometry = Polyline.fromJSON(favourite.geometry);

                        setSelector({
                            tool: favourite.tool,
                            label: favourite.label,
                            geometry
                        });

                        const g = new Graphic({
                            geometry,
                            symbol: {
                                type: 'simple-line',
                                color: [0, 0, 0],
                                width: 2
                            }
                        });
                        sketchLayer.add(g);

                        if (view !== undefined)
                            await zoomToExtent({
                                view,
                                extent: geometry.extent
                            });

                        const response = await queryFeatureLayer({
                            layer: activeLayer,
                            geometry,
                            spatialRelationship: 'intersects',
                            returnGeometry: true
                        });
                        setFeatures(response.features);
                    }}
                    onRemove={(id: string) => {
                        removeFavourite(id);
                    }}
                />
            )}
        </>
    );
};

export default App;
